// The TERMINAL tab: a real shell in the batch's sandbox (xterm.js over the agent's WebSocket; `sh` in the container,
// `npm install`, `node`, `git` all work). Offline: a note on how to start the agent.
import { useEffect, useRef } from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'
import { agentStartHint } from '@/lib/agent'

export function TerminalView({ url, dark, note }: { url: string | null; dark: boolean; note?: string | null }) {
  const host = useRef<HTMLDivElement>(null)
  const term = useRef<Terminal | null>(null)

  useEffect(() => {
    const el = host.current
    if (!el || !url) return
    const t = new Terminal({ fontSize: 12.5, fontFamily: 'ui-monospace, Cascadia Mono, Consolas, monospace', cursorBlink: true, convertEol: false, scrollback: 2000, theme: dark ? { background: '#020617', foreground: '#e2e8f0' } : { background: '#ffffff', foreground: '#0f172a', cursor: '#0f172a' } })
    const fit = new FitAddon()
    t.loadAddon(fit)
    t.open(el)
    fit.fit()
    term.current = t
    const ws = new WebSocket(url.replace(/cols=\d+&rows=\d+/, `cols=${t.cols}&rows=${t.rows}`))
    ws.binaryType = 'arraybuffer'
    ws.onmessage = (m) => t.write(typeof m.data === 'string' ? m.data : new Uint8Array(m.data as ArrayBuffer))
    ws.onclose = () => t.write('\r\n[the shell closed]\r\n')
    ws.onerror = () => t.write('\r\n[could not reach the agent]\r\n')
    const onData = t.onData((d) => { if (ws.readyState === 1) ws.send(d) })
    let gone = false
    const ro = new ResizeObserver(() => {
      if (gone) return
      try { fit.fit() } catch { /* the terminal is going away */ }
      if (ws.readyState === 1) ws.send(`\x01resize:${t.cols}x${t.rows}`)
    })
    ro.observe(el)
    return () => {
      gone = true
      ro.disconnect()
      onData.dispose()
      // a socket still connecting is closed once it opens (closing it earlier logs a browser warning)
      if (ws.readyState === WebSocket.CONNECTING) ws.onopen = () => ws.close()
      else ws.close()
      term.current = null
      // xterm's renderer may still have a frame queued; disposing on the next tick avoids its "dimensions" error
      setTimeout(() => t.dispose(), 0)
    }
  }, [url]) // eslint-disable-line react-hooks/exhaustive-deps

  // dark mode: re-theme the open terminal, never re-create it
  useEffect(() => {
    if (term.current) term.current.options.theme = dark ? { background: '#020617', foreground: '#e2e8f0' } : { background: '#ffffff', foreground: '#0f172a', cursor: '#0f172a' }
  }, [dark])

  if (!url) {
    return (
      <div className="p-2 font-mono text-[12px] text-muted" data-testid="pg-terminal">
        No sandbox: the terminal needs a running sandbox. {note ?? agentStartHint()}
      </div>
    )
  }
  return <div ref={host} className="h-full w-full" data-testid="pg-terminal" />
}
